import ReconnectingWebSocket, {
  Options as ReconnectingWebSocketOptions,
} from "reconnecting-websocket";
import { Providers } from "../../SofyaTranscriber";
import { OracleTranscriptionAdapter } from "./adapters/OracleTranscriptionAdapter";
import { BatchTranscriptionAdapter } from "./adapters/BatchTranscriptionAdapter";
import { WhisperTranscriptionAdapter } from "./adapters/WhisperTranscriptionAdapter";
import {
  DEFAULT_BATCH_REPROCESS_PATH,
  ResolvedWhisperConnectionContext,
} from "./batchReprocess";
import { ITranscriptionService } from "./interfaces/ITranscriptionService";
import {
  DirectModeAuth,
  applyAuthToRealtimeUrl,
  assertValidAuthConfig,
  redactAuthConfig,
  resolveAuthHeaders,
  resolveBearerToken,
  resolveRealtimeProtocols,
} from "./auth";

const resolveRuntimeBaseUrl = () =>
  typeof window !== "undefined" && window.location?.href
    ? window.location.href
    : "http://localhost/";

const normalizeBatchProtocol = (protocol: string) => {
  if (protocol === "wss:") {
    return "https:";
  }

  if (protocol === "ws:") {
    return "http:";
  }

  return protocol;
};

const languageSelector = (language?: string) => {
  switch (language) {
    case "pt-BR":
      return "portuguese";
    case "en-US":
      return "english";
    case "es-ES":
      return "spanish";
    default:
      return language ? "multilingual" : undefined;
  }
};

const cloneHeaders = (headers?: Record<string, string>) =>
  headers ? { ...headers } : undefined;

export class TranscriptionServiceFactory {
  static async create(provider: Providers, config: any): Promise<ITranscriptionService> {
    console.log("TranscriptionServiceFactory.create config", {
      provider,
      config: redactAuthConfig(config),
    });

    // Fails before any connection attempt when the credential cannot be used.
    assertValidAuthConfig(
      config?.auth as DirectModeAuth | undefined,
      config?.mode
    );

    if (config?.mode === "batch") {
      switch (provider) {
        case "sofya_as_service":
        case "stt_wvad":
        case "sofya_whisper_flow": {
          const resolvedConnectionContext =
            this.resolveWhisperConnectionContext(config);

          return new BatchTranscriptionAdapter({
            ...config,
            resolvedConnectionContext,
          });
        }
        default:
          throw new Error(
            "Primary batch mode is only supported for Whisper providers."
          );
      }
    }

    switch (provider) {
      case "sofya_compliance":
      case "oracle":
        return new OracleTranscriptionAdapter(config);
      case "sofya_as_service":
      case "stt_wvad":
      case "sofya_whisper_flow": {
        const resolvedConnectionContext =
          this.resolveWhisperConnectionContext(config);
        const ws = this.connectToWebSocket(
          resolvedConnectionContext.realtimeUrl,
          config?.resilience,
          resolveRealtimeProtocols(
            config?.protocols,
            config?.auth as DirectModeAuth | undefined
          )
        );

        return new WhisperTranscriptionAdapter({
          ...config,
          provider,
          resolvedConnectionContext,
          ws,
        });
      }
      default:
        throw new Error("Provider não suportado");
    }
  }

  private static resolveWhisperConnectionContext(
    config: any
  ): ResolvedWhisperConnectionContext {
    if (!config?.endpoint?.endpoint) {
      throw new Error("Endpoint missing for Whisper provider.");
    }

    const realtimeUrl = new URL(config.endpoint.endpoint, resolveRuntimeBaseUrl());
    const transcriptionLanguage = languageSelector(config.language);
    const translationLanguage = languageSelector(config.translation_lang);

    if (transcriptionLanguage) {
      realtimeUrl.searchParams.set(
        "transcription_language",
        transcriptionLanguage
      );
    }

    if (translationLanguage) {
      realtimeUrl.searchParams.set("translation_language", translationLanguage);
    }

    if (config.external_id) {
      realtimeUrl.searchParams.set("x-external-id", config.external_id);
    }

    // `record` só entra na URL quando definido explicitamente: `undefined`
    // deixa o padrão do ambiente decidir. Como `x-external-id`, o valor da
    // config prevalece sobre um `record=` já presente no endpoint.
    if (typeof config.record === "boolean") {
      realtimeUrl.searchParams.set("record", String(config.record));
    }

    applyAuthToRealtimeUrl(realtimeUrl, config?.auth as DirectModeAuth | undefined);

    const batchBaseUrl = new URL("/", realtimeUrl.toString());
    batchBaseUrl.protocol = normalizeBatchProtocol(realtimeUrl.protocol);

    return {
      realtimeUrl: realtimeUrl.toString(),
      batchBaseUrl: batchBaseUrl.toString(),
      batchDefaultUrl:
        config?.mode === "batch"
          ? realtimeUrl.toString()
          : new URL(
              DEFAULT_BATCH_REPROCESS_PATH,
              batchBaseUrl.toString()
            ).toString(),
      authToken: resolveBearerToken(
        config?.auth as DirectModeAuth | undefined,
        config.token
      ),
      externalId: config.external_id,
      transcriptionLanguage,
      translationLanguage,
      headers: resolveAuthHeaders(
        cloneHeaders(config.headers),
        config?.auth as DirectModeAuth | undefined
      ),
    };
  }

  private static connectToWebSocket(
    endpoint: string,
    resilience?: {
      connectTimeoutMs?: number;
      reconnectBaseDelayMs?: number;
      reconnectMaxDelayMs?: number;
      reconnectDelayGrowFactor?: number;
      minConnectionUptimeMs?: number;
      maxReconnectAttempts?: number;
    },
    protocols?: string | string[]
  ): ReconnectingWebSocket {
    const options: ReconnectingWebSocketOptions = {
      connectionTimeout: resilience?.connectTimeoutMs ?? 10000,
      minReconnectionDelay: resilience?.reconnectBaseDelayMs ?? 1000,
      maxReconnectionDelay: resilience?.reconnectMaxDelayMs ?? 10000,
      reconnectionDelayGrowFactor:
        resilience?.reconnectDelayGrowFactor ?? 1.3,
      minUptime: resilience?.minConnectionUptimeMs ?? 5000,
      maxRetries: resilience?.maxReconnectAttempts ?? 96,
    };

    return new ReconnectingWebSocket(() => endpoint, protocols ?? [], options);
  }
}
